import { createSlice } from '@reduxjs/toolkit'

export interface PlaceResult {
  place_id: string
  name: string
  formatted_address: string
  address_components: {
    long_name: string
    short_name: string
    types: google.maps.places.AddressComponent['types']
  }[]
  geometry: {
    location: {
      lat: number
      lng: number
    }
    viewport: {
      northeast: {
        lat: number
        lng: number
      }
      southwest: {
        lat: number
        lng: number
      }
    }
  }
}

interface initialStateProps {
  city: PlaceResult | null
  district: PlaceResult | null
  street: PlaceResult | null
  radius?: number
  center: google.maps.LatLngLiteral
}

const initialState: initialStateProps = {
  city: null,
  district: null,
  street: null,
  center: { lat: 50.4501, lng: 30.5234 },
}

const latLngToLatLngLiteral = (latLng: google.maps.LatLngLiteral | undefined) => {
  if (!latLng) {
    return initialState.center
  } else {
    return latLng
  }
}

const calculateRadius = (place: PlaceResult): number | undefined => {
  if (place?.geometry?.location && place?.geometry?.viewport) {
    const centerLocation = place.geometry.location
    const bounds = place.geometry.viewport
    const northeast = bounds.northeast

    return google.maps.geometry.spherical.computeDistanceBetween(centerLocation, northeast)
  }
  return undefined
}

const handlePlaceChanged = (
  state: initialStateProps,
  payload: PlaceResult | null,
  placeName: string,
) => {
  switch (placeName) {
    case 'city':
      if (!payload) {
        state.center = initialState.center
        state.radius = 1000
      } else if (!state.district && !state.street) {
        state.center = latLngToLatLngLiteral(payload.geometry?.location)
        state.radius = calculateRadius(payload)
      }
      break
    case 'district':
      if (!payload) {
        if (state.city) {
          state.center = latLngToLatLngLiteral(state.city.geometry?.location)
          state.radius = calculateRadius(state.city)
        }
      } else if (!state.street) {
        state.center = latLngToLatLngLiteral(payload.geometry?.location)
        state.radius = calculateRadius(payload)
      }
      break
    case 'street':
      if (!payload) {
        if (state.district) {
          state.center = latLngToLatLngLiteral(state.district.geometry?.location)
          state.radius = calculateRadius(state.district)
        } else if (state.city) {
          state.center = latLngToLatLngLiteral(state.city.geometry?.location)
          state.radius = calculateRadius(state.city)
        }
      } else {
        state.center = latLngToLatLngLiteral(payload.geometry?.location)
        state.radius = calculateRadius(payload)
      }
      break
    default:
      break
  }
}

interface PayloadProps {
  payload: PlaceResult | null
}

const createPropertySlice = createSlice({
  name: 'createProperty',
  initialState,
  reducers: {
    setCity(state, { payload }: PayloadProps) {
      state.district = null
      state.street = null
      handlePlaceChanged(state, payload, 'city')
      state.city = payload
    },
    setDistrict(state, { payload }: PayloadProps) {
      state.street = null
      handlePlaceChanged(state, payload, 'district')
      state.district = payload
    },
    setStreet(state, { payload }: PayloadProps) {
      payload
      handlePlaceChanged(state, payload, 'street')
      state.street = payload
    },
    resetGoogleApiState() {
      return initialState
    },
  },
})

export const { setCity, setDistrict, setStreet, resetGoogleApiState } = createPropertySlice.actions
export default createPropertySlice.reducer
